// Implements: F-DSG-002, F-UIX-020, F-UIX-007
/**
 * The closed font choice as the mockup v3 select (`#p-ff`): the eleven Google families first, then
 * the four system stacks, each group an `<optgroup>`. Pure presentation.
 *
 * Native for the same reason as `ClosedSelect` (keyboard, type-ahead, announcement), drawn with the
 * shared select's sheet. The person picks a family name; the value sent is always one of the closed
 * literals. `extra` holds options outside both groups (the editor's "Personalizada" when titles and
 * text use different fonts).
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import styles from "../../../shared/ui/atoms/Select.module.css";
import { GOOGLE_FONT_STACKS, SYSTEM_FONT_STACKS } from "../model/profileModel";
import { FONT_LABELS } from "./editorOptions";

export interface FontSelectProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled: boolean;
  readonly extra?: readonly { readonly value: string; readonly label: string }[] | undefined;
}

export function FontSelect({ id, value, onChange, disabled, extra = [] }: FontSelectProps): ReactElement {
  const { t } = useTranslation();
  return (
    <select
      id={id}
      className={styles.select}
      value={value}
      disabled={disabled}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    >
      <optgroup label={t("design.font.group.google")}>
        {GOOGLE_FONT_STACKS.map((font) => (
          <option key={font} value={font}>
            {t(FONT_LABELS[font])}
          </option>
        ))}
      </optgroup>
      <optgroup label={t("design.font.group.system")}>
        {SYSTEM_FONT_STACKS.map((font) => (
          <option key={font} value={font}>
            {t(FONT_LABELS[font])}
          </option>
        ))}
      </optgroup>
      {extra.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
